import { CURRENT, FORMER } from '@/components/company-contact/constants/companyMembership';
import { JobContactsQuery } from '@/graphql/generated';
import { Contact, ExtendedContact } from '@/types';

type Connections = JobContactsQuery['viewer'];

type ConnectionType = 'firstDegreeCurrentContacts' | 'firstDegreeFormerContacts' | 'secondDegreeContacts';

const FIRST_DEGREE_CURRENT = 'firstDegreeCurrentContacts';
const FIRST_DEGREE_FORMER = 'firstDegreeFormerContacts';
const SECOND_DEGREE = 'secondDegreeContacts';
const MAX_CONTACTS = 3;

const priorities: ConnectionType[] = [
  FIRST_DEGREE_CURRENT,
  FIRST_DEGREE_CURRENT,
  FIRST_DEGREE_FORMER,
  FIRST_DEGREE_CURRENT,
  FIRST_DEGREE_FORMER,
  FIRST_DEGREE_FORMER,
  SECOND_DEGREE,
];

const decorateContact = (criteria: ConnectionType, contact: Contact): ExtendedContact => {
  switch (criteria) {
    case FIRST_DEGREE_CURRENT:
      return { ...contact, user: { ...contact.user, connectionDegree: 1 }, companyMembership: CURRENT };
    case FIRST_DEGREE_FORMER:
      return { ...contact, user: { ...contact.user, connectionDegree: 1 }, companyMembership: FORMER };
    default:
      return { ...contact, user: { ...contact.user, connectionDegree: 2 } };
  }
};

const getSecondDegreeContacts = (connections: Connections, excludeSecondDegreeContacts = false) => {
  if (excludeSecondDegreeContacts || !connections) return;
  return connections.secondDegreeContacts?.edges;
};

const normalizeConnections = (connections: Connections, excludeSecondDegreeContacts = false) => {
  return connections && Object.keys(connections).length > 0
    ? {
        firstDegreeCurrentContacts: [...(connections.firstDegreeCurrentContacts?.edges ?? [])],
        firstDegreeFormerContacts: [...(connections.firstDegreeFormerContacts?.edges ?? [])],
        secondDegreeContacts: [...(getSecondDegreeContacts(connections, excludeSecondDegreeContacts) ?? [])],
      }
    : {};
};

const enoughSpaceForContact = (selectedContacts: ExtendedContact[], criteria: ConnectionType) =>
  selectedContacts.length < MAX_CONTACTS - 1 || criteria !== SECOND_DEGREE;

export const filterCompanyContacts = (connections: Connections, excludeSecondDegreeContacts = false) => {
  const availableConnections = normalizeConnections(connections, excludeSecondDegreeContacts);

  const selectedContacts: ExtendedContact[] = [];

  for (let i = 0; i < priorities.length && selectedContacts.length < MAX_CONTACTS; i++) {
    const criteria = priorities[i];

    if (availableConnections[criteria]) {
      const connection = availableConnections?.[criteria]?.shift();

      if (enoughSpaceForContact(selectedContacts, criteria) && connection?.node?.user) {
        selectedContacts.push(decorateContact(criteria, connection.node as Contact));
      }
    }
  }

  return selectedContacts;
};
